import { getI18nLang } from '@yandex-int/i18n';
import { OAuthAppSettings, UpdateOAuthAppSettings } from 'client/model/oauth';
import { Operation } from 'client/model/operation';
import { UserReview } from 'client/model/reviews';
import { DashboardRowMeta, MonitoringQuery, SolomonLabels } from 'client/model/solomon';
import { getIsYangoRequest } from 'client/utils';
import queryString from 'query-string';

import { routes } from '../lib/routes';
import { Image } from '../model/image';
import { ImageResource, ResourcesStatus } from '../model/resources';
import { InstallableAppSettings, Settings } from '../model/settings';
import {
    Channel,
    DonationSettings,
    HomepageFields,
    IAppCard,
    IInstallableAppPageInfo,
    InstallableApp,
    Skill,
} from '../model/skill';
import { ApkInfoWithTargets, AppsMetaInfo, FeatureFlags, LicenseAgreement, PhraseCommonness, Snapshot } from '../types';
import { CloudFunction } from '../types/cloud';
import { ImagesListResult, ItemsPageContinues } from '../types/utils';

import * as devicesApi from './services/devices';
import * as devicesTestingApi from './services/devicesTesting';
import * as granetApi from './services/grammar';
import * as shareApi from './services/share';
import * as skillTestApi from './services/skillTest';
import { callApi, callPublicApi, translateOperations } from './utils';
export { ApiError } from './error';
export { granetApi };
export { skillTestApi };
export { devicesApi };
export { shareApi };
export { devicesTestingApi };

export function getAppsMeta() {
    return callApi<AppsMetaInfo>({ method: 'GET', url: routes.app.appStoreApi('apps/meta-info') });
}

export function getAppsCards({
    page,
    pageSize,
    channels,
    listNotOwnedApps,
    onlyPublished,
    query,
}: {
    page?: number;
    pageSize?: number;
    channels: Channel[];
    listNotOwnedApps?: boolean;
    onlyPublished?: boolean;
    query?: string;
}) {
    return callApi<{ cards: IAppCard[]; paging_info: IInstallableAppPageInfo }>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/cards`),
        queryParams: {
            page,
            pageSize,
            channels,
            listNotOwnedApps,
            onlyPublished,
            query,
        },
    });
}

export function createApp<T extends Channel>(appName: string, channel: T) {
    return callApi<T extends Channel.TvApp | Channel.AutoApp | Channel.SmartScreenApp ? { skillId: string } : Skill>({
        method: 'POST',
        url: routes.app.appStoreApi('/apps'),
        data: {
            channel,
            language: getI18nLang(),
            isYangoConsole: getIsYangoRequest(),
            appName,
        },
    });
}

export function deleteApp(id: string, channel: string) {
    const appIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`/apps/${appIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function stopApp(id: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillIdURLEncoded}/stop`),
        queryParams: { channel: channel },
    });
}

export function forceStopApp(id: string, channel: Channel, data: { comment: string }) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillIdURLEncoded}/force-stop`),
        data,
        queryParams: { channel: channel },
    });
}

export function getInstallableApp(id: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function updateInstallableAppDraft(id: string, data: Partial<InstallableAppSettings>) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/update`),
        data,
    });
}

export function installableAppUploadImage(
    skillId: string,
    channel: Channel,
    file: File,
    imageType: 'appTileImage' | 'appScreenshot',
) {
    const skillIdURLEncoded = encodeURIComponent(skillId);
    const form = new FormData();

    form.append('file', file);

    return callApi<{ id: string; url: string }>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/upload-image`),
        data: form,
        queryParams: {
            channel: channel,
            type: imageType,
        },
    });
}

export function installableAppUploadApk(params: {
    appId: string;
    channel: Channel;
    file: File;
    versionNotes?: string;
}) {
    const form = new FormData();

    const versionNotesIfPresent = params.versionNotes ? encodeURIComponent(params.versionNotes) : undefined;
    const appId = encodeURIComponent(params.appId);

    form.append('file', params.file);

    return callApi<ApkInfoWithTargets>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${appId}/draft/upload-apk`),
        queryParams: {
            channel: params.channel,
            versionNotes: versionNotesIfPresent,
        },
        data: form,
    });
}

export function installableAppUploadLicenseAgreement(skillId: string, channel: Channel, file: File) {
    const form = new FormData();

    form.append('file', file);

    return callApi<LicenseAgreement>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillId}/draft/upload-draft-user-agreement`),
        data: form,
        queryParams: {
            channel: channel,
        },
    });
}

export function requestInstallableAppReview(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/request-moderation`),
        queryParams: { channel: channel },
    });
}

export function requestCancelInstallableAppModeration(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/cancel-moderation`),
        queryParams: { channel: channel },
    });
}

export function requestDeploy(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/request-deploy`),
        queryParams: { channel: channel },
    });
}

export function getFeatureFlags(skillId: string, channel: Channel): Promise<FeatureFlags> {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi<FeatureFlags>({
        method: 'GET',
        url: routes.app.appStoreApi(`feature-flags/${skillIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function updateSkillFeatureFlags(skillId: string, channel: Channel, data: string[]) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callPublicApi<object>({
        method: 'PUT',
        url: routes.app.appStoreApi(`skill-feature-flags/${skillIdURLEncoded}`),
        data,
        queryParams: { channel: channel },
    });
}

export function updateUserFeatureFlags(userId: string, channel: Channel, data: Record<string, boolean>) {
    return callPublicApi<object>({
        method: 'PUT',
        url: routes.app.appStoreApi(`user-feature-flags/${userId}`),
        data,
        queryParams: { channel: channel },
    });
}

export function createOrUpdateOAuthApp(data: UpdateOAuthAppSettings) {
    return callApi<OAuthAppSettings>({ method: 'POST', url: routes.app.appStoreApi('oauth/apps'), data });
}

export function getOAuthApp(id: string) {
    return callApi<OAuthAppSettings>({ method: 'GET', url: routes.app.appStoreApi(`oauth/apps/${id}`) });
}

export function getHostsForSuggest(params: { query: string }) {
    const encodedQuery = encodeURIComponent(params.query);

    return callApi<string[]>({ method: 'GET', url: routes.app.appStoreApi('hosts', `?query=${encodedQuery}`) });
}

export function getMonitoringData(skillId: string, query: MonitoringQuery) {
    return callApi<DashboardRowMeta[]>({
        method: 'GET',
        url: routes.app.appStoreApi(`/monitoring/apps/${skillId}`, `?${queryString.stringify(query)}`),
        timeout: __appConfig.monitoring.timeoutMs,
    });
}

export function getMonitoringLabels() {
    return callApi<SolomonLabels>({ method: 'GET', url: routes.app.appStoreApi('/monitoring/page-data') });
}

export function getSkillReviews(skillId: string, channel: Channel, page: number) {
    return callApi<ItemsPageContinues<UserReview>>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillId}/reviews`),
        queryParams: {
            channel: channel,
            page: page,
        },
    });
}

export async function getOperationsTranslated(skillId: string) {
    const response = await callApi<Array<Operation>>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillId}/operations`),
    });

    return translateOperations(response);
}

export function apiSetActiveRevision<T extends Channel>(skillId: string, channel: T, revisionId: string) {
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillId}/set-active-revision`),
        data: {
            revisionId,
        },
        queryParams: {
            channel: channel,
        },
    });
}

export function createSkill(channel: Channel) {
    return callApi<Skill>({
        method: 'POST',
        url: routes.app.api('skills'),
        data: {
            channel,
            language: getI18nLang(),
            isYangoConsole: getIsYangoRequest(),
        },
    });
}

export function getSkill(id: string) {
    return callApi<Skill>({ method: 'GET', url: routes.app.appStoreApi(`apps/${id}`) });
}

export function updateDraft(id: string, data: Partial<Settings>, channel: Channel) {
    return callApi<Skill>({
        method: 'PATCH',
        url:
            channel === Channel.AliceSkill || channel === Channel.SmartHome
                ? routes.app.appStoreApi(`apps/${id}/draft/update`)
                : routes.app.api(`skills/${id}/draft`),
        data: {
            ...data,
            channel,
        },
    });
}

export function attachOauth(id: string, oauthAppId: string, channel: Channel) {
    return callApi<Skill>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${id}/oauthApp`),
        queryParams: { channel },
        data: {
            oauthAppId,
        },
    });
}

export function deattachOauth(id: string, channel: Channel) {
    return callApi<Skill>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`apps/${id}/oauthApp`),
        queryParams: { channel },
    });
}

export function updateSkillFlags(id: string, channel: Channel, data: Partial<Skill>) {
    return callApi<Skill>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`apps/${id}/flags`),
        queryParams: { channel },
        data,
    });
}

export function updateMainpageSettings(id: string, data: HomepageFields, channel: Channel) {
    return callApi<Skill>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`apps/${id}/homepage`),
        queryParams: { channel },
        data,
    });
}

export function requestSkillReview(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/request-moderation`),
        queryParams: { channel },
    });
}

export function cancelSkillReview(id: string, channel: Channel) {
    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${id}/draft/cancel-moderation`),
        queryParams: { channel },
    });
}

interface RequestSkillDeployBody {
    draft: Skill['draft'];
    published: boolean;
}

export function requestSkillDeploy(id: string) {
    return callApi<RequestSkillDeployBody>({ method: 'POST', url: routes.app.api(`skills/${id}/release`) });
}

export function uploadImage(skillId: string, file: File, channel: Channel) {
    const form = new FormData();

    form.append('file', file);

    return callApi<Image>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillId}/draft/upload-logo`),
        data: form,
        queryParams: {
            channel,
        },
    });
}

export async function getSnapshot() {
    const response = await callApi<Snapshot>({ method: 'GET', url: routes.app.appStoreApi('snapshot') });

    const operations = translateOperations(response.operations);

    return {
        ...response,
        operations,
    };
}

export function subscribe() {
    return callApi({ method: 'POST', url: routes.app.appStoreApi('subscription') });
}

export function unsubscribe(token?: string | (string | null)[]) {
    return callApi({ method: 'POST', url: routes.app.appStoreApi('unsubscribe'), data: { token } });
}

export function newsSubscribe() {
    return callApi({ method: 'POST', url: routes.app.appStoreApi('news-subscription') });
}

export function newsUnsubscribe(token?: string) {
    return callApi({ method: 'DELETE', url: routes.app.appStoreApi('news-subscription'), data: { token } });
}

export function validateModerationTestToken(skillId: string, token: string | (string | null)[]) {
    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillId}/draft/moderation/validate-token`),
        data: { token },
    });
}

export function getPhraseCommonness(phrase: string) {
    const encodedPhrase = encodeURIComponent(phrase);

    return callApi<PhraseCommonness>({
        method: 'GET',
        url: routes.app.appStoreApi('phrase-commonness', `?phrase=${encodedPhrase}`),
    });
}

export function getSkillImages(skillId: string, page: number) {
    return callPublicApi<ImagesListResult>({
        method: 'GET',
        url: routes.app.appStoreApi(`public/v1/apps/${skillId}/images?page=${page}`),
    }).then((x) => ({
        items: x.images,
        total: x.total,
        limit: x.limit,
    }));
}

export function deleteSkillImage(skillId: string, imageId: string) {
    return callPublicApi({ method: 'DELETE', url: routes.app.appStoreApi(`public/v1/apps/${skillId}/image/${imageId}`) });
}

export function uploadSkillImage(skillId: string, file: File) {
    const form = new FormData();
    form.append('file', file);

    return callPublicApi<{ image: ImageResource }>({
        method: 'POST',
        url: routes.app.appStoreApi(`public/v1/apps/${skillId}/image`),
        data: form,
    }).then((x) => x.image);
}

export function getResourcesStatus() {
    return callPublicApi<ResourcesStatus>({ method: 'GET', url: routes.app.appStoreApi('public/v1/status') });
}

export function getCloudFunctions() {
    return callApi<CloudFunction[]>({ method: 'GET', url: routes.app.appStoreApi('/cloud-functions') });
}

export function validateCloudFunction(functionId: string) {
    return callApi<'ok'>({ method: 'POST', url: routes.app.appStoreApi(`/cloud-functions/${functionId}/validate`) });
}

export function updateDonationSettings(skillId: string, channel: Channel, settings: DonationSettings) {
    return callApi<'ok'>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`apps/${skillId}/donation/settings`),
        queryParams: { channel },
        data: {
            walletNumber: settings.walletNumber,
            defaultSum: Number(settings.defaultSum),
        },
    });
}

export function deleteDonationSettings(skillId: string, channel: Channel) {
    return callApi<'ok'>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`apps/${skillId}/donation/settings`),
        queryParams: { channel },
    });
}
